/**
 * Boot-time normalization of a deep-linked ingest URL, so a signed download URL survives
 * redux-first-router's (RFR) parsing intact.
 *
 * Why this exists (VE-18631): the `/ingest?url=<SIGNED_URL>` deep link carries a signed URL
 * (Azure SAS, Google Drive, ...) that has its OWN query string — e.g.
 * `…/x.mp4?sv=…&se=…&sig=kza%2B…%3D&rscd=…`. When callers don't percent-encode that inner URL,
 * it reaches the browser with a nested `?` and raw `&`. RFR builds `pathname + search` and then
 * does `currentPath.split('?')[1]` (see `redux-first-router/pure-utils/pathToAction.js`), so it
 * keeps only the segment between the FIRST and SECOND `?` and discards everything after the inner
 * `?` — the whole signature — BEFORE any `querySerializer.parse` runs. A custom serializer can't
 * recover data RFR has already dropped.
 *
 * Fix: before RFR reads `window.location`, rewrite the address bar so the inner URL is a single,
 * properly percent-encoded `url` value with no nested `?`/`&`. RFR's split then sees one `?`, and
 * the stock `query-string` parser decodes the value back to the full signed URL. The inner URL's
 * own encoding (the SAS `sig`'s `%2B`/`%2F`/`%3D`) round-trips unharmed.
 *
 * `url` is treated as the TERMINAL query parameter (it always is in real inbound links — its
 * `&`s run to the end of the string). This module must run BEFORE `connectRoutes()`.
 */

// Matches the `url` key only as a genuine top-level query key (start of string or right after an
// `&`), so it can't match `redirect=…url=…`, `foo_url=`, or an encoded `url%3D` inside a value.
const URL_KEY = /(^|&)url=/;

/**
 * Strip a single pair of wrapping quotes (literal `"` or percent-encoded `%22`) that some callers
 * put around the URL value.
 */
const trimWrappingQuotes = (value: string): string => {
  if (value.startsWith('%22') && value.endsWith('%22') && value.length >= 6) {
    return value.slice(3, -3);
  }
  if (value.startsWith('"') && value.endsWith('"') && value.length >= 2) {
    return value.slice(1, -1);
  }
  return value;
};

/**
 * Given the raw location parts (as `window.location` reports them — `search` still percent-encoded
 * and beginning with `?`), return a normalized `pathname + search + hash` string in which the
 * inner signed URL is collapsed into one encoded `url` value. Returns `null` when no rewrite is
 * needed. Pure and window-free so it can be unit-tested directly.
 */
export const normalizeIngestSearch = (
  pathname: string,
  search: string,
  hash: string
): string | null => {
  const query = search.replace(/^\?/, '');
  const match = URL_KEY.exec(query);
  if (match === null) {
    return null;
  }

  const leading = match[1] ?? '';
  const urlKeyStart = (match.index ?? 0) + leading.length;
  const before = query.slice(0, urlKeyStart).replace(/&$/, '');
  const rawValue = query.slice(urlKeyStart + 'url='.length);

  const inner = trimWrappingQuotes(rawValue);

  // Only an un-encoded URL (literal `://`) whose own `?`/`&` would be truncated by RFR needs
  // rewriting. Already-encoded values (no literal `://`) and plain URLs without a nested query
  // are well-formed already and left untouched.
  const needsRewrite =
    inner.includes('://') && (inner.includes('?') || inner.includes('&'));
  if (!needsRewrite) {
    return null;
  }

  const rebuiltQuery = [before, `url=${encodeURIComponent(inner)}`]
    .filter(Boolean)
    .join('&');

  return `${pathname}?${rebuiltQuery}${hash}`;
};

/**
 * Rewrite `window.location` in place (via `history.replaceState`, so no navigation/reload) when a
 * deep-linked ingest URL would otherwise be mangled by redux-first-router. No-op outside the
 * browser or when the current URL needs no change. MUST be called before `connectRoutes()`.
 */
export const normalizeIngestUrlInLocation = (): void => {
  if (typeof window === 'undefined' || !window.location) {
    return;
  }

  const { pathname, search, hash } = window.location;
  const next = normalizeIngestSearch(pathname, search, hash);
  if (next !== null) {
    window.history.replaceState(window.history.state, '', next);
  }
};
